import type { Label, Status, WorkspaceMember } from '@/contexts/WorkspaceContext'
import { getMemberDisplayName } from '@/utils/memberDisplayName'

export type GroupingMode = 'sections' | 'assignee' | 'label' | 'status'

// Every mode except the default one. 'sections' is rendered by the project page's own path —
// it carries the drag & drop wiring, the per-section callbacks and the section options menu,
// none of which a derived group has. Keeping it out of groupTasks means there is no unused
// second way to build the section view that could drift from the real one.
export type GroupedMode = Exclude<GroupingMode, 'sections'>

export const GROUPING_MODES: GroupingMode[] = ['sections', 'assignee', 'label', 'status']

export const GROUPING_MODE_TITLES: Record<GroupingMode, string> = {
    sections: 'Sections',
    assignee: 'Assignee',
    label: 'Label',
    status: 'Status',
}

// Titles of the "no value" bucket per axis. 'Unassigned' is not a new string — it is what
// AssigneeDropdown and the Team page already call a task with no assignee.
export const NO_ASSIGNEE_TITLE = 'Unassigned'
export const NO_LABEL_TITLE = 'No label'
export const NO_STATUS_TITLE = 'No status'

// Shown for a member whose username is empty. Same fallback the comment list uses.
const UNKNOWN_MEMBER_TITLE = 'Unknown'

// Reserved key for the "no value" bucket. Prefixed so it can never collide with a Firestore id.
const NO_VALUE_KEY = '__none__'

export interface TaskGroup<T> {
    /** Stable React key — a member/label/status id, or the reserved no-value key. */
    key: string
    title: string
    tasks: T[]
}

/** The only fields grouping reads. Structural, so tests don't have to build whole Tasks. */
export interface GroupableTask {
    assigneeId?: string | null
    statusId?: string | null
    labels?: string[] | null
}

export interface GroupingContext {
    members: WorkspaceMember[]
    labels: Label[]
    statuses: Status[]
}

/**
 * Split tasks into display groups along one axis.
 *
 * Three rules hold on every axis:
 *  - Empty groups are dropped. A workspace can have twenty labels and eight members while a
 *    project uses three of each, and a wall of empty headings is worse than a short list.
 *  - The "no value" bucket is always last, and it also absorbs values that no longer resolve
 *    (a removed member, a deleted status, a label id left behind by a deleted label). Deleting
 *    any of those does not rewrite task docs, so dangling ids are normal — and a task that
 *    matched no group would silently vanish from a project the user is looking at.
 *  - Group order comes from the workspace, not from the tasks: `status.order` / `label.order`
 *    for those two, alphabetical for people.
 *
 * Note that grouping by label DUPLICATES a task carrying several labels into each of its
 * groups. Labels are the one multi-valued axis, and showing a task once would mean hiding it
 * from a group whose heading promises it.
 */
export function groupTasks<T extends GroupableTask>(
    tasks: T[],
    mode: GroupedMode,
    context: GroupingContext
): TaskGroup<T>[] {
    switch (mode) {
        case 'assignee':
            return groupByAssignee(tasks, context.members)
        case 'label':
            return groupByLabel(tasks, context.labels)
        case 'status':
            return groupByStatus(tasks, context.statuses)
    }
}

function groupByAssignee<T extends GroupableTask>(
    tasks: T[],
    members: WorkspaceMember[]
): TaskGroup<T>[] {
    // A task's assigneeId may hold either the user id or the membership doc id — the same two
    // keys WorkspaceContext's getMember accepts — so index both and normalise to the user id.
    const byAnyId = new Map<string, WorkspaceMember>()
    for (const member of members) {
        byAnyId.set(member.userId, member)
        byAnyId.set(member.id, member)
    }

    const buckets = new Map<string, T[]>()
    for (const task of tasks) {
        const member = task.assigneeId ? byAnyId.get(task.assigneeId) : undefined
        push(buckets, member ? member.userId : NO_VALUE_KEY, task)
    }

    // Sorted by what the heading actually shows, so the list reads A→Z on screen.
    const ordered = [...members].sort((a, b) =>
        memberTitle(a).localeCompare(memberTitle(b))
    )

    const groups: TaskGroup<T>[] = []
    const seen = new Set<string>()
    for (const member of ordered) {
        if (seen.has(member.userId)) continue
        seen.add(member.userId)
        const bucket = buckets.get(member.userId)
        if (bucket?.length) {
            groups.push({ key: member.userId, title: memberTitle(member), tasks: bucket })
        }
    }

    return appendNoValueGroup(groups, buckets, NO_ASSIGNEE_TITLE)
}

function groupByLabel<T extends GroupableTask>(
    tasks: T[],
    labels: Label[]
): TaskGroup<T>[] {
    const live = new Set(labels.map(label => label.id))

    const buckets = new Map<string, T[]>()
    for (const task of tasks) {
        // Resolved against the live labels, so a task whose only label was deleted counts as
        // unlabelled rather than as a member of a group that no longer has a heading.
        const ids = (task.labels ?? []).filter(id => live.has(id))
        if (ids.length === 0) {
            push(buckets, NO_VALUE_KEY, task)
            continue
        }
        for (const id of ids) push(buckets, id, task)
    }

    const groups: TaskGroup<T>[] = []
    for (const label of [...labels].sort((a, b) => a.order - b.order)) {
        const bucket = buckets.get(label.id)
        if (bucket?.length) {
            groups.push({ key: label.id, title: label.name || 'Untitled', tasks: bucket })
        }
    }

    return appendNoValueGroup(groups, buckets, NO_LABEL_TITLE)
}

function groupByStatus<T extends GroupableTask>(
    tasks: T[],
    statuses: Status[]
): TaskGroup<T>[] {
    const live = new Set(statuses.map(status => status.id))

    // Most task-creation paths write `statusId: ''`, so an unset status is ordinary data rather
    // than an edge case — and the task window already renders those tasks as the todo status
    // (see TaskModalStatus). Grouping has to agree with it, or the same task reads "Todo" in
    // the window and sits under "No status" in the list. A deleted status resolves the same
    // way. Only a workspace with no todo status at all falls through to the no-value bucket.
    const todoStatus = statuses.find(status => status.systemType === 'todo')

    const buckets = new Map<string, T[]>()
    for (const task of tasks) {
        const resolved = task.statusId && live.has(task.statusId)
            ? task.statusId
            : todoStatus?.id ?? NO_VALUE_KEY
        push(buckets, resolved, task)
    }

    const groups: TaskGroup<T>[] = []
    for (const status of [...statuses].sort((a, b) => a.order - b.order)) {
        const bucket = buckets.get(status.id)
        if (bucket?.length) {
            groups.push({ key: status.id, title: status.name || 'Untitled', tasks: bucket })
        }
    }

    return appendNoValueGroup(groups, buckets, NO_STATUS_TITLE)
}

function memberTitle(member: WorkspaceMember): string {
    return getMemberDisplayName(member) || UNKNOWN_MEMBER_TITLE
}

function push<T>(buckets: Map<string, T[]>, key: string, task: T): void {
    const bucket = buckets.get(key)
    if (bucket) bucket.push(task)
    else buckets.set(key, [task])
}

function appendNoValueGroup<T>(
    groups: TaskGroup<T>[],
    buckets: Map<string, T[]>,
    title: string
): TaskGroup<T>[] {
    const bucket = buckets.get(NO_VALUE_KEY)
    if (bucket?.length) groups.push({ key: NO_VALUE_KEY, title, tasks: bucket })
    return groups
}
